
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Key, Mail, MoreHorizontal, Shield, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ChangeUserPasswordDialog } from "./ChangeUserPasswordDialog";
import { useState } from "react";

type Member = {
  id: string;
  user_id: string;
  organization_id: string;
  role: string;
  created_at: string;
  email: string | null;
  full_name: string | null;
  phone: string | null;
};

export function OrganizationMemberList() {
  const { currentOrganization } = useOrganization();
  const { toast } = useToast();
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [memberToChangePassword, setMemberToChangePassword] = useState<Member | null>(null);

  const { data: members, isLoading, refetch } = useQuery({
    queryKey: ["organization-members", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) return [];
      
      // Use the database function to get members with profile data
      const { data, error } = await supabase
        .rpc("get_organization_members", {
          org_id: currentOrganization.id
        });
      
      if (error) throw error;
      return data as Member[];
    },
    enabled: !!currentOrganization,
  });

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from("organization_members")
        .update({ role: newRole })
        .eq("id", memberId);
      
      if (error) throw error;
      
      await refetch();
      
      toast({
        title: "Role Updated",
        description: "Member role has been updated successfully",
      });
    } catch (error) {
      console.error("Error updating role:", error);
      toast({
        variant: "destructive",
        title: "Failed to update role",
        description: error instanceof Error ? error.message : "An error occurred",
      });
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    
    try {
      const { error } = await supabase
        .from("organization_members")
        .delete()
        .eq("id", memberToRemove.id);
      
      if (error) throw error;
      
      await refetch();
      setMemberToRemove(null);
      
      toast({
        title: "Member Removed",
        description: "The member has been removed from the organization",
      });
    } catch (error) {
      console.error("Error removing member:", error);
      toast({
        variant: "destructive",
        title: "Failed to remove member",
        description: error instanceof Error ? error.message : "An error occurred",
      });
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading members...</p>
    </div>;
  }

  if (!members?.length) {
    return <div className="text-center p-8">
      <User className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
      <p className="text-lg font-medium">No members found</p>
      <p className="text-muted-foreground">Your organization doesn't have any members yet.</p>
    </div>;
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell className="font-medium">
                <div className="flex items-center">
                  <User className="mr-2 h-4 w-4" />
                  {member.full_name || "Unnamed User"}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center text-muted-foreground">
                  <Mail className="mr-2 h-4 w-4" />
                  {member.email || "-"}
                </div>
              </TableCell>
              <TableCell>
                <Badge
                  variant={member.role === 'admin' ? "default" : "outline"}
                >
                  {member.role === 'admin' ? (
                    <div className="flex items-center">
                      <Shield className="mr-1 h-3 w-3" />
                      Admin
                    </div>
                  ) : (
                    'Member'
                  )}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {member.role !== 'admin' && (
                      <DropdownMenuItem
                        onClick={() => handleRoleChange(member.id, 'admin')}
                      >
                        Make Admin
                      </DropdownMenuItem>
                    )}
                    {member.role === 'admin' && (
                      <DropdownMenuItem
                        onClick={() => handleRoleChange(member.id, 'member')}
                      >
                        Remove Admin
                      </DropdownMenuItem>
                     )}
                     <DropdownMenuItem
                       onClick={() => setMemberToChangePassword(member)}
                     >
                       <Key className="mr-2 h-4 w-4" />
                       Change Password
                     </DropdownMenuItem>
                     <DropdownMenuItem
                       className="text-destructive focus:text-destructive"
                       onClick={() => setMemberToRemove(member)}
                     >
                       Remove Member
                     </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={!!memberToRemove}
        onOpenChange={() => setMemberToRemove(null)}
        title="Remove Member"
        description={`Are you sure you want to remove ${memberToRemove?.email || 'this member'} from the organization?`}
        onConfirm={handleRemoveMember}
      />

      <ChangeUserPasswordDialog
        open={!!memberToChangePassword}
        onOpenChange={() => setMemberToChangePassword(null)}
        userId={memberToChangePassword?.user_id || ""}
        userEmail={memberToChangePassword?.email || ""}
      />
    </>
  );
}
